import { Box } from '@mui/material';
import { Drawer, drawerType } from '../../../../../packages/ui/organisms/drawer';
import {
  btnType,
  Button,
  IconButton,
  Input,
  inputSize,
  inputType,
  Text,
  TextError,
} from '../../../../../packages';
import { useFormik } from 'formik';
import { sx } from './GoalDrawer.styles';

export const GoalDrawer = ({ isOpen, onClose, goal, onSubmit }) => {
  const { errors, values, handleChange, handleSubmit, touched, setFieldValue, ...rest } = useFormik({
    initialValues: {
      name: goal?.name || '',
      details: goal?.details || '',
      objectives:
        goal?.objectives?.map(obj => {
          return obj.details || '';
        }) || [],
    },
    onSubmit: values => {
      // Map objectives back to objects with IDs where possible
      const mappedObjectives = values.objectives.map((details, index) => {
        // Try to preserve the original objective ID if it exists
        const originalObj = goal?.objectives?.[index];
        const objId = originalObj?.id || null;
        const isNew = !originalObj?.id;
        const isCompleted = originalObj?.isCompleted || false;

        return {
          id: objId,
          details,
          new: isNew,
          isCompleted,
        };
      });

      const result = {
        ...goal,
        name: values.name,
        details: values.details,
        objectives: mappedObjectives.filter(obj => obj.details.trim() !== ''),
      };

      onSubmit(result);
      onClose();
    },
  });

  // Check if maximum objectives (3) have been reached
  const hasMaxObjectives = values.objectives.length >= 3;

  const handleAddObjective = () => {
    // Only add a new objective if we haven't reached the maximum
    if (!hasMaxObjectives) {
      setFieldValue('objectives', [...values.objectives, '']);
    }
  };

  const handleRemoveObjective = index => {
    setFieldValue(
      'objectives',
      values.objectives.filter((_, i) => i !== index),
    );
  };

  return (
    <Drawer open={isOpen} onClose={onClose} variant={drawerType.NORMAL} sx={sx.drawer}>
      <Box sx={sx.wrapper}>
        <Box sx={sx.header}>
          <Text sx={sx.headerTitle}>{goal.new ? 'Add' : 'Edit'} Goal</Text>
          <IconButton icon="close" onClick={onClose} sx={sx.backBtn} />
        </Box>
        <Box sx={sx.form}>
          <form onSubmit={handleSubmit}>
            <Box sx={sx.formContent}>
              <Box sx={sx.section}>
                <Box flexDirection="column" display="flex">
                  <Input
                    value={values.name}
                    name="name"
                    onChange={handleChange}
                    variant={inputType.DEFAULT}
                    size={inputSize.M}
                    fullWidth
                    placeholder="Goal name"
                    sx={sx.valuesInput}
                  />
                  <TextError errorMsg={touched.name ? errors.name?.toString() : null} />
                  <Input
                    value={values.details}
                    name="details"
                    onChange={handleChange}
                    variant={inputType.DEFAULT}
                    size={inputSize.M}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Goal details"
                    sx={sx.valuesInput}
                  />
                  <TextError errorMsg={touched.details ? errors.details?.toString() : null} />
                </Box>
              </Box>
              <Box sx={sx.section}>
                <Box display="flex" justifyContent="space-between">
                  <Text sx={sx.sectionTitle}>Objectives</Text>
                  {!hasMaxObjectives && (
                    <IconButton icon="plus" onClick={handleAddObjective} sx={sx.addObjective}>
                      Add New
                    </IconButton>
                  )}
                </Box>
                <Box flexDirection="column" display="flex">
                  {values.objectives.map((objective, index) => (
                    <Box key={index} sx={sx.objectiveItem}>
                      <Input
                        value={objective}
                        onChange={e => {
                          const newObjectives = [...values.objectives];
                          newObjectives[index] = e.target.value;
                          setFieldValue('objectives', newObjectives);
                        }}
                        variant={inputType.DEFAULT}
                        size={inputSize.M}
                        fullWidth
                        placeholder="Objective details"
                        sx={sx.valuesInput}
                      />
                      <IconButton
                        icon="close"
                        onClick={() => handleRemoveObjective(index)}
                        sx={sx.removeObjective}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box sx={sx.footer}>
              <Button variant={btnType.TEXT} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Drawer>
  );
};
